import { Editor, EditorRef } from '@/components/editor/Editor';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { useCurrentUser } from '@/features/users/users.hooks';
import { cn } from '@/lib/utils';
import { Position } from '@certhon/models';
import bytes from 'bytes';
import { MapPin, Paperclip, Send, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { GeolocationModal } from './GeolocationModal';
import { useScrollIntoViewOnFocus } from '@/hooks/useScrollIntoViewOnFocus';

interface MessageComposerProps {
  onSendMessage: (message: string) => Promise<void>;
  onUploadAttachments: (files: File[]) => Promise<void>;
  onAddLocation?: (location: { position: Position; label: string }) => void;
  disabled?: boolean;
  placeholder?: string;
  onEditLastMessage?: () => void;
}

interface PendingFile {
  file: File;
  id: string;
  previewUrl?: string;
}

export function MessageComposer({
  onSendMessage,
  onUploadAttachments,
  onAddLocation,
  disabled = false,
  placeholder = 'Write a message...',
  onEditLastMessage,
}: MessageComposerProps) {
  const user = useCurrentUser();
  const [message, setMessage] = useState('');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<EditorRef>(null);
  const composerRef = useScrollIntoViewOnFocus<HTMLDivElement>();

  // Cleanup object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      pendingFiles.forEach((pf) => {
        if (pf.previewUrl) {
          URL.revokeObjectURL(pf.previewUrl);
        }
      });
    };
  }, [pendingFiles]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim() && pendingFiles.length === 0) return;
    if (message.trim()) {
      onSendMessage(message.trim()).then(() => {
        editorRef.current?.clear();
      });
    }

    if (pendingFiles.length > 0) {
      onUploadAttachments(pendingFiles.map((pf) => pf.file)).then(() => {
        setPendingFiles([]);
      });
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files).map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      previewUrl: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined,
    }));

    setPendingFiles((prev) => [...prev, ...newFiles]);
  };

  const handleFilePaste = (files: File[]) => {
    if (files.length === 0) return;

    const newFiles = files.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      previewUrl: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined,
    }));

    setPendingFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();

    // Only show the file drop overlay if we're dragging files, not tickets
    const hasTicketData =
      e.dataTransfer.types.includes('application/x-entity') ||
      e.dataTransfer.types.includes('application/x-entities');

    if (!hasTicketData) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removePendingFile = (id: string) => {
    setPendingFiles((prev) => prev.filter((pf) => pf.id !== id));
  };

  const handleLocationSubmit = (location: {
    position: Position;
    label: string;
  }) => {
    if (onAddLocation) {
      onAddLocation(location);
    }
  };

  const canSubmit = message.trim().length > 0 || pendingFiles.length > 0;

  return (
    <div ref={composerRef} className="mt-6">
      <div className="flex gap-x-3">
        <UserAvatar {...user!} className="mt-1" size="md" />

        <form onSubmit={handleSubmit} className="flex-1">
          <div
            className={cn(
              'overflow-hidden rounded-lg border transition-colors',
              isDragging && 'border-primary bg-primary/5',
              'focus-within:ring-ring/50 focus-within:ring-[3px]',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Pending files display */}
            {pendingFiles.length > 0 && (
              <div className="bg-muted/50 border-b px-3 py-2">
                <div className="flex flex-wrap gap-2">
                  {pendingFiles.map((pendingFile) => (
                    <div
                      key={pendingFile.id}
                      className="bg-background flex items-center gap-2 rounded px-2 py-1 text-sm"
                    >
                      {pendingFile.previewUrl ? (
                        <img
                          src={pendingFile.previewUrl}
                          alt={pendingFile.file.name}
                          className="h-10 w-10 rounded object-cover"
                        />
                      ) : (
                        <Paperclip className="h-3 w-3" />
                      )}
                      <span className="max-w-32 truncate">
                        {pendingFile.file.name}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        (
                        {bytes(pendingFile.file.size, {
                          decimalPlaces: 1,
                          unitSeparator: ' ',
                        })}
                        )
                      </span>
                      <button
                        type="button"
                        onClick={() => removePendingFile(pendingFile.id)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Editor
              ref={editorRef}
              namespace="message-composer"
              // defaultValue={message} // this is empty anyway
              onChange={(value) => setMessage(value)}
              placeholder={placeholder}
              disabled={disabled}
              onPasteFiles={handleFilePaste}
              // className="text-foreground placeholder:text-muted-foreground block w-full resize-none
              //   border-0 bg-transparent px-3 py-2 focus-visible:ring-0"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.stopPropagation();
                  e.preventDefault();
                  if (canSubmit) {
                    handleSubmit(e);
                  }
                } else if (e.key === 'ArrowUp' && (e.ctrlKey || e.metaKey)) {
                  e.stopPropagation();
                  e.preventDefault();
                  if (onEditLastMessage) {
                    onEditLastMessage();
                  }
                }
              }}
            />

            {/* Drag overlay */}
            {isDragging && (
              <div
                className="bg-primary/10 border-primary absolute inset-0 z-10 flex items-center
                  justify-center rounded-lg border-2 border-dashed"
              >
                <div className="text-center">
                  <Upload className="text-primary mx-auto mb-2 h-8 w-8" />
                  <p className="text-primary font-medium">Drop files here</p>
                </div>
              </div>
            )}

            <div className="bg-muted/50 flex items-center justify-between border-t px-3 py-2">
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileSelect(e.target.files)}
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() => fileInputRef.current?.click()}
                  className="h-8 w-8 p-0"
                >
                  <Paperclip className="h-4 w-4" />
                  <span className="sr-only">Attach files</span>
                </Button>
                {onAddLocation && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => setShowLocationModal(true)}
                    className="h-8 w-8 p-0"
                  >
                    <MapPin className="h-4 w-4" />
                    <span className="sr-only">Add location</span>
                  </Button>
                )}
              </div>

              <Button
                type="submit"
                size="sm"
                disabled={disabled || !canSubmit}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                Send
              </Button>
            </div>
          </div>
        </form>
      </div>

      <GeolocationModal
        open={showLocationModal}
        onOpenChange={setShowLocationModal}
        onSubmit={handleLocationSubmit}
        disabled={disabled}
      />
    </div>
  );
}
